import { useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { Radio, RadioGroup, Text } from '../Ant';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import NoData from '../dashboard/noData';
import { GeneralEmpty } from '../icons';
import { useUser } from '../../context/UserContext';
import { useLanguage } from '../../context/LanguageContext';
import { useUpdateCurrentUser } from '../../hooks/useUpdateCurrentUser';
import {
  REGIONAL_FORMAT_TAGS,
  SUGGESTED_REGIONAL_FORMAT_TAGS,
} from '../../constants/regional-formats';
import { displayFullCountryName, displayLanguageName } from '../../constants/utilityFunctions';
import ListSectionHeading from '../listSectionHeading/listSectionHeading';

// e.g. 'sv-SE' -> "Swedish (Sweden)" — the language subtag names the format, the country
// subtag (in brackets) names the region it's tied to, matching the mobile app's picker.
const regionalFormatLabel = (
  tag: string,
  appLanguage: string,
  languageNames?: Intl.DisplayNames,
  countryNames?: Intl.DisplayNames,
): string => {
  const [languageSubtag, countrySubtag] = tag.split('-');
  const languageName = displayLanguageName(languageSubtag, appLanguage, languageNames);
  const countryName =
    displayFullCountryName(countrySubtag, appLanguage, countryNames) ?? countrySubtag;
  return `${languageName} (${countryName})`;
};

interface IRegionOption {
  tag: string;
  label: string;
}

const renderRegionOptions = (items: IRegionOption[]) =>
  items.map((item) => (
    <Radio key={item.tag} value={item.tag} data-testid={`region-option-${item.tag}`}>
      <Text $fontsize="13px" $color="title" $weight={500} $lineheight="20px">
        {item.label}
      </Text>
    </Radio>
  ));

const matchesQuery = (option: IRegionOption, query: string): boolean =>
  option.label.toLowerCase().includes(query) || option.tag.toLowerCase().includes(query);

const RegionList = () => {
  const { t } = useTranslation();
  const { userDetails } = useUser();
  const { getAppLanguageCode } = useLanguage();
  const { mutate: updateUser } = useUpdateCurrentUser();
  const appLanguage = getAppLanguageCode();

  const [selectedRegion, setSelectedRegion] = useState(userDetails?.uiRegionalFormat);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSelectedRegion(userDetails?.uiRegionalFormat);
  }, [userDetails?.uiRegionalFormat]);

  const regionOptions = useMemo<IRegionOption[]>(() => {
    const languageNames = new Intl.DisplayNames([appLanguage], { type: 'language' });
    const countryNames = new Intl.DisplayNames([appLanguage], { type: 'region' });
    return REGIONAL_FORMAT_TAGS.map((tag) => ({
      tag,
      label: regionalFormatLabel(tag, appLanguage, languageNames, countryNames),
    })).sort((a, b) => a.label.localeCompare(b.label, appLanguage));
  }, [appLanguage]);

  const suggestedOptions = useMemo<IRegionOption[]>(() => {
    const currentRegion = userDetails?.uiRegionalFormat;
    const suggestedTags = currentRegion
      ? [...new Set([currentRegion, ...SUGGESTED_REGIONAL_FORMAT_TAGS])]
      : SUGGESTED_REGIONAL_FORMAT_TAGS;
    return suggestedTags.map((tag) => ({
      tag,
      label: regionalFormatLabel(tag, appLanguage),
    }));
  }, [userDetails?.uiRegionalFormat, appLanguage]);

  const query = searchValue.trim().toLowerCase();
  const matchingOptions = query
    ? regionOptions.filter((option) => matchesQuery(option, query))
    : regionOptions;
  const suggestedRegions = query
    ? suggestedOptions.filter((option) => matchesQuery(option, query))
    : suggestedOptions;
  const suggestedTags = new Set(suggestedRegions.map((option) => option.tag));
  const remainingRegions = matchingOptions.filter((option) => !suggestedTags.has(option.tag));

  const handleChange = (tag: string) => {
    const previousRegion = selectedRegion;
    setSelectedRegion(tag);
    updateUser({ uiRegionalFormat: tag }, { onError: () => setSelectedRegion(previousRegion) });
  };
  return (
    <Flex vertical gap={10} className="flex-grow">
      <SearchInputWithFilters
        searchTitle={t('common:profile.searchRegionalFormat')}
        handleSearch={setSearchValue}
      />
      {suggestedRegions.length === 0 && remainingRegions.length === 0 ? (
        <div
          className="flex-grow d-flex justify-center items-center"
          role="status"
          aria-live="polite"
        >
          <NoData
            header={t('common:profile.noRegionalFormats')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<GeneralEmpty />}
          />
        </div>
      ) : (
        <RadioGroup
          value={selectedRegion}
          onChange={(e) => handleChange(e.target.value as string)}
          className="d-flex fd-column grouped-list overflow-y-auto region-selection"
          data-testid="region-list"
        >
          {suggestedRegions.length > 0 && (
            <div data-testid="suggested-region-list">
              <ListSectionHeading title={t('common:profile.suggestedFormats')} />
              {renderRegionOptions(suggestedRegions)}
            </div>
          )}
          {remainingRegions.length > 0 && <ListSectionHeading title={t('common:all')} />}
          {renderRegionOptions(remainingRegions)}
        </RadioGroup>
      )}
    </Flex>
  );
};

export default RegionList;
